import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Tags as TagsIcon } from 'lucide-react';
import { CATEGORY_COLOURS, type CategoryColour } from '@fint/validation';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { PageHeader } from '@/app/DashboardLayout';
import { Button, Card, ColourChip, ColourPicker, EmptyState, ErrorState, Input, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader, SkeletonTable, Table, TBody, TD, TH, THead, TR } from '@/features/common/ui';

interface TagRow {
  id: string;
  name: string;
  colour: string;
  description: string | null;
  isActive: boolean;
  usageCount: number;
}

/**
 * Managing the tag vocabulary.
 *
 * It lives in Settings rather than the main nav because a tag is configuration,
 * not a working surface - and the nav already carries fourteen items with a
 * "More" sheet on a phone.
 *
 * The empty state does the teaching this whole feature turns on: a tag is for
 * the things that cut ACROSS a category, and anyone who wants to break their
 * spending down wants a category instead. Saying it here is cheaper than saying
 * it after somebody has filed six months of spend under a tag.
 */
export function TagsPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();

  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<TagRow | 'new' | null>(null);

  const orgId = org?.id;
  const tags = useQuery({
    queryKey: ['tags', orgId, showArchived],
    enabled: !!orgId,
    queryFn: () => api.get<{ items: TagRow[] }>(`/tags${qs({ includeInactive: showArchived })}`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tags', orgId] });
  };

  const save = useMutation({
    mutationFn: (input: { id?: string; name: string; colour: CategoryColour; description?: string }) =>
      input.id
        ? api.patch<TagRow>(`/tags/${input.id}`, {
            name: input.name,
            colour: input.colour,
            description: input.description || null,
          })
        : api.post<TagRow>('/tags', {
            name: input.name,
            colour: input.colour,
            description: input.description || undefined,
          }),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  const archive = useMutation({
    mutationFn: (tag: TagRow) => api.patch<TagRow>(`/tags/${tag.id}`, { isActive: !tag.isActive }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (tag: TagRow) => api.delete<{ ok: true }>(`/tags/${tag.id}`),
    onSuccess: invalidate,
  });

  const rows = tags.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Tags"
        subtitle="Labels you can put on an expense or an invoice, to find them again later."
        actions={
          perms.can('tag', 'create') ? (
            <Button onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" /> New tag
            </Button>
          ) : null
        }
      />
      {/* The same gutter every other screen has. Without it the content sat
          flush against the sidebar, on these four pages and no others. */}
      <div className="space-y-4 px-4 pb-10 sm:px-5">
        <div className="flex gap-1.5">
          {[false, true].map((archived) => (
            <button
              key={String(archived)}
              type="button"
              aria-pressed={showArchived === archived}
              onClick={() => setShowArchived(archived)}
              className={
                showArchived === archived
                  ? 'rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary'
                  : 'rounded-full px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted'
              }
            >
              {archived ? 'Including archived' : 'Active'}
            </button>
          ))}
        </div>

        {tags.isPending ? <SkeletonTable rows={4} /> : null}
        {tags.isError ? <ErrorState error={tags.error} onRetry={() => { void tags.refetch(); }} /> : null}

        {tags.isSuccess && rows.length === 0 ? (
          <EmptyState
            icon={TagsIcon}
            title="No tags yet"
            description={
              'A tag is a label you can put on an expense or an invoice - "Diwali campaign", ' +
              '"Reimbursable" - so you can find them together later. A record can have several. ' +
              'Tags are for the things that cut across your categories; if you want to break your ' +
              'spending down, use a category instead.'
            }
            action={
              perms.can('tag', 'create') ? (
                <Button onClick={() => setEditing('new')}>Create your first tag</Button>
              ) : null
            }
          />
        ) : null}

        {rows.length > 0 ? (
          <Card>
            <Table>
              <THead>
                <TR>
                  <TH>Tag</TH>
                  <TH>On</TH>
                  <TH className="text-right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((tag) => (
                  <TR key={tag.id}>
                    <TD>
                      <span className="flex items-center gap-2">
                        <ColourChip colour={tag.colour} name={tag.name} />
                        {!tag.isActive ? (
                          <span className="text-xs text-muted-foreground">Archived</span>
                        ) : null}
                      </span>
                    </TD>
                    <TD className="text-muted-foreground">
                      {tag.usageCount > 0 ? (
                        // The manager is also a way INTO the data, which is what
                        // makes a count worth printing rather than a status.
                        <Link to={`/expenses?tag=${tag.id}`} className="hover:underline">
                          {tag.usageCount} {tag.usageCount === 1 ? 'record' : 'records'}
                        </Link>
                      ) : (
                        'Nothing yet'
                      )}
                    </TD>
                    <TD className="text-right">
                      {perms.can('tag', 'edit') ? (
                        <Button variant="ghost" size="sm" onClick={() => setEditing(tag)}>
                          Edit
                        </Button>
                      ) : null}
                      {perms.can('tag', 'edit') ? (
                        <Button variant="ghost" size="sm" onClick={() => archive.mutate(tag)}>
                          {tag.isActive ? 'Archive' : 'Restore'}
                        </Button>
                      ) : null}
                      {/*
                        Delete appears ONLY at a usage count of zero. A tag on 47
                        records cannot be deleted - the API refuses it - and
                        offering a button whose only outcome is an error is worse
                        than not offering it.
                      */}
                      {perms.can('tag', 'delete') && tag.usageCount === 0 ? (
                        <Button variant="ghost" size="sm" onClick={() => remove.mutate(tag)}>
                          Delete
                        </Button>
                      ) : null}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
        ) : null}

        {remove.isError ? (
          // The refusal names a count - "that tag is on 47 records" - and it is
          // rendered a long way from the Delete button that produced it.
          <p role="alert" className="text-sm text-destructive">
            {(remove.error as ApiError).message}
          </p>
        ) : null}

        {editing ? (
          <TagComposer
            tag={editing === 'new' ? undefined : editing}
            onClose={() => setEditing(null)}
            onSubmit={(input) => save.mutate(input)}
            pending={save.isPending}
            error={save.error instanceof ApiError ? save.error : undefined}
          />
        ) : null}
      </div>
    </>
  );
}

function TagComposer({
  tag,
  onClose,
  onSubmit,
  pending,
  error,
}: {
  tag?: TagRow;
  onClose: () => void;
  onSubmit: (input: { id?: string; name: string; colour: CategoryColour; description?: string }) => void;
  pending: boolean;
  error?: ApiError;
}) {
  useEscapeToClose(onClose);
  const [name, setName] = useState(tag?.name ?? '');
  const [description, setDescription] = useState(tag?.description ?? '');
  const [colour, setColour] = useState<CategoryColour>(
    (CATEGORY_COLOURS.find((c) => c === tag?.colour) ?? CATEGORY_COLOURS[1]) as CategoryColour,
  );

  return (
    <Sheet labelledBy="tag-sheet-title" onClose={onClose}>
      <SheetHeader titleId="tag-sheet-title" title={tag ? 'Edit tag' : 'New tag'} onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit({ id: tag?.id, name, colour, description });
        }}
      >
        <SheetBody>
          <div className="space-y-1">
            <label htmlFor="tag-name" className="text-sm font-medium">
              Name
            </label>
            <span aria-hidden="true" className="text-destructive"> *</span>
            <Input
              id="tag-name"
              // The sheet opens for one purpose; landing focus anywhere else
              // makes a keyboard user tab past a heading and a close button to
              // reach the only field that matters.
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={60}
              required
              aria-describedby="tag-name-hint"
            />
            <p id="tag-name-hint" className="text-xs text-muted-foreground">
              Up to 60 characters. Shown on every record you put it on.
            </p>
          </div>

          <div className="space-y-1.5">
            <span className="text-sm font-medium">Colour</span>
            <ColourPicker value={colour} onChange={setColour} />
            <p className="text-xs text-muted-foreground">
              Colour helps you spot it. The name is what people read.
            </p>
          </div>

          <div className="space-y-1">
            <label htmlFor="tag-desc" className="text-sm font-medium">
              Description
            </label>
            <Input
              id="tag-desc"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={200}
            />
          </div>

          <div className="space-y-1.5">
            <span className="text-sm font-medium">Preview</span>
            <div>
              <ColourChip colour={colour} name={name || 'Your tag'} />
            </div>
          </div>

          {error ? (
            // Announced. The 409 "that tag is on 47 records" is the whole reason
            // the button did nothing, and it renders below the fold of a form.
            <p role="alert" className="text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" disabled={pending || !name.trim()}>
            {pending ? 'Saving…' : tag ? 'Save changes' : 'Create tag'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
